import React, { useMemo } from 'react';

/**
 * Renders the multi-pass synthesis "Confidence Audit" — every High/Medium/Low
 * confidence claim from the swarm's exec summary, grouped for an at-a-glance
 * evidence review.
 *
 * Shape of `confidenceSummary`:
 *   {
 *     high_confidence_claims: string[],
 *     medium_confidence_claims: string[],
 *     low_confidence_claims: string[]
 *   }
 */

const TAG_STYLES = {
  FACT:      { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  INFERENCE: { bg: 'bg-amber-100',   text: 'text-amber-800' },
  ESTIMATE:  { bg: 'bg-rose-100',    text: 'text-rose-800' },
};
const CONF_STYLES = {
  high:   { bg: 'bg-green-100',  text: 'text-green-800' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  low:    { bg: 'bg-red-100',    text: 'text-red-800' },
};

const renderInlineTags = (line, keyBase) => {
  const tagRe = /\[(FACT|INFERENCE|ESTIMATE),\s*(high|medium|low)\]/gi;
  const parts = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = tagRe.exec(line)) !== null) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    const kind = m[1].toUpperCase();
    const conf = m[2].toLowerCase();
    const ks = TAG_STYLES[kind];
    const cs = CONF_STYLES[conf];
    parts.push(
      <span
        key={`tag-${keyBase}-${i++}`}
        className="inline-flex items-center gap-1 align-middle mx-0.5 text-[9px] font-bold uppercase tracking-wider"
      >
        <span className={`px-1 py-0.5 rounded ${ks.bg} ${ks.text}`}>{kind}</span>
        <span className={`px-1 py-0.5 rounded ${cs.bg} ${cs.text}`}>{conf}</span>
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < line.length) parts.push(line.slice(last));
  return parts;
};

const ConfidenceAudit = ({ confidenceSummary }) => {
  const empty = useMemo(() => {
    if (!confidenceSummary) return true;
    const h = confidenceSummary.high_confidence_claims?.length || 0;
    const m = confidenceSummary.medium_confidence_claims?.length || 0;
    const l = confidenceSummary.low_confidence_claims?.length || 0;
    return (h + m + l) === 0;
  }, [confidenceSummary]);

  if (empty) return null;

  const high = confidenceSummary.high_confidence_claims || [];
  const med = confidenceSummary.medium_confidence_claims || [];
  const low = confidenceSummary.low_confidence_claims || [];

  return (
    <div
      data-testid="confidence-audit-panel"
      className="bg-white rounded-xl shadow-md border border-gray-200 p-6"
    >
      <h3 className="text-lg font-bold text-gray-900 mb-1">Confidence Audit</h3>
      <p className="text-xs text-gray-500 mb-4">
        Every meaningful claim above is tagged with its evidence level. Use this list as a quick QA pass before sharing the report with clients.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="confidence-audit-high">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-[10px] font-bold uppercase">High</span>
            <span className="text-xs text-gray-600">{high.length} claims</span>
          </div>
          <ul className="space-y-1.5">
            {high.map((c, i) => (
              <li key={`hi-${i}`} className="text-[12px] text-gray-700 leading-snug">
                {renderInlineTags(c, `hi-${i}`)}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-testid="confidence-audit-medium">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 text-[10px] font-bold uppercase">Medium</span>
            <span className="text-xs text-gray-600">{med.length} claims</span>
          </div>
          <ul className="space-y-1.5">
            {med.map((c, i) => (
              <li key={`me-${i}`} className="text-[12px] text-gray-700 leading-snug">
                {renderInlineTags(c, `me-${i}`)}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="confidence-audit-low">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-bold uppercase">Low</span>
            <span className="text-xs text-gray-600">{low.length} claims</span>
          </div>
          <ul className="space-y-1.5">
            {low.map((c, i) => (
              <li key={`lo-${i}`} className="text-[12px] text-gray-700 leading-snug">
                {renderInlineTags(c, `lo-${i}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConfidenceAudit;
